import { useEffect, useState, createContext, useContext, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CatalogPage from "./pages/CatalogPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import AddProductPage from "./pages/AddProductPage";
import ProfilePage from "./pages/ProfilePage";
import MyReservationsPage from "./pages/MyReservationsPage";
import FAQsPage from "./pages/FAQsPage";
import LocationPage from "./pages/LocationPage";
import AdminPage from "./pages/AdminPage";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import StatsPage from "./pages/StatsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (response.data && response.data.user) {
      setUser(response.data.user);
    }
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData, { withCredentials: true });
    if (response.data && response.data.user) {
      setUser(response.data.user);
    }
    return response.data;
  };

  const processGoogleSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
    setUser(response.data.user);
    return response.data;
  };

  const completeProfile = async (profileData) => {
    const response = await axios.post(`${API}/auth/complete-profile`, profileData, { withCredentials: true });
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, processGoogleSession, completeProfile, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component - Handles Google OAuth redirect
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processGoogleSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const result = await processGoogleSession(sessionId);
          // Check if profile is complete
          if (!result.user.profile_complete && !result.user.dni) {
            navigate("/completar-perfil", { state: { user: result.user }, replace: true });
          } else {
            navigate("/catalogo", { state: { user: result.user }, replace: true });
          }
        } catch (error) {
          toast.error("Error al procesar la autenticación");
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processSession();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#374B67] mx-auto mb-4"></div>
        <p className="text-slate-600">Procesando autenticación...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireCompleteProfile = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#374B67]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireCompleteProfile && !user.profile_complete && !user.dni) {
    return <Navigate to="/completar-perfil" replace />;
  }

  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/catalogo" replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL fragment - MUST be synchronous during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registro" element={<RegisterPage />} />
      <Route path="/catalogo" element={<CatalogPage />} />
      <Route path="/producto/:productId" element={<ProductDetailPage />} />
      <Route path="/faqs" element={<FAQsPage />} />
      <Route path="/localizacion" element={<LocationPage />} />
      <Route path="/impacto" element={<StatsPage />} />
      
      {/* Protected Routes */}
      <Route path="/completar-perfil" element={
        <ProtectedRoute requireCompleteProfile={false}>
          <CompleteProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/anadir-producto" element={
        <ProtectedRoute>
          <AddProductPage />
        </ProtectedRoute>
      } />
      <Route path="/perfil" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/mis-reservas" element={
        <ProtectedRoute>
          <MyReservationsPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <AdminPage />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API };
